// src/components/StyleShop.js
import React, {
    useEffect,
    useState,
    useMemo,
} from "react";
import { useWallet } from "@solana/wallet-adapter-react";

import "./SimulacrumExplorerStyle.css";

// Firestore
import { db } from "../firebaseConfig";
import {
    collection,
    onSnapshot,
    addDoc,
    doc,
    setDoc,
    serverTimestamp,
    arrayUnion,
} from "firebase/firestore";

// Umi / Metaplex Core
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import {
    generateSigner,
    sol,
    lamports,
    publicKey as umiPublicKey,
    transactionBuilder,
} from "@metaplex-foundation/umi";
import { walletAdapterIdentity } from "@metaplex-foundation/umi-signer-wallet-adapters";
import { base58 } from "@metaplex-foundation/umi/serializers";
import {
    mplCore,
    create as createCoreNft,
} from "@metaplex-foundation/mpl-core";

// Irys uploader
import { irysUploader } from "@metaplex-foundation/umi-uploader-irys";

// SOL transfer helper
import { transferSol } from "@metaplex-foundation/mpl-toolbox";

// Irys endpoints
const DEVNET_IRYS = "https://devnet.irys.xyz";
const MAINNET_IRYS = "https://node1.irys.xyz";

// Wallet that receives SOL when someone buys a style
const STYLE_SERVICE_WALLET =process.env.REACT_APP_STYLE_SERVICE_WALLET;

// Fixed network fee added to the base style price
const FIXED_FEE_SOL = 0.005;

export default function StyleShop() {
    const { publicKey, wallet, connected } = useWallet();

    const [styles, setStyles] = useState([]);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [status, setStatus] = useState("");

    const rpcEndpoint =
        process.env.REACT_APP_RPC_ENDPOINT ||
        "https://api.devnet.solana.com";
    const isDevnet = rpcEndpoint.includes("devnet");
    const irysAddress = isDevnet ? DEVNET_IRYS : MAINNET_IRYS;

    // Load active styles from Firestore
    useEffect(() => {
        const ref = collection(db, "styles_master");
        const unsub = onSnapshot(ref, (snap) => {
            const items = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((s) => s.active);
            setStyles(items);
        });
        return () => unsub();
    }, []);

    // Umi instance with Core + wallet + Irys
    const umi = useMemo(() => {
        const u = createUmi(rpcEndpoint).use(mplCore());
        if (wallet?.adapter) {
            u.use(walletAdapterIdentity(wallet.adapter));
        }
        u.use(irysUploader({ address: irysAddress }));
        return u;
    }, [wallet?.adapter, rpcEndpoint, irysAddress]);

    const handlePurchase = async (style) => {
        setStatus("");

        if (!connected || !publicKey || !wallet?.adapter) {
            setStatus("❌ Connect your wallet to purchase this style.");
            return;
        }

        if (!STYLE_SERVICE_WALLET) {
            setStatus(
                "❌ STYLE_SERVICE_WALLET env var is not set. Define REACT_APP_STYLE_SERVICE_WALLET."
            );
            return;
        }

        if (!style?.previewImageUrl) {
            setStatus("❌ This style has no preview image configured.");
            return;
        }

        try {
            setIsPurchasing(true);
            setStatus("🎨 Preparing style NFT…");

            // 1) Build metadata using the Firebase Storage URL directly
            const metadata = {
                name: style.name || style.id,
                description:
                    style.description || "Style skin for the NFT Wallet Explorer.",
                image: style.previewImageUrl,
                external_url: "",
                attributes: [
                    { trait_type: "Style ID", value: style.id },
                    { trait_type: "Theme", value: style.themeKey || "" },
                ],
                properties: {
                    files: [
                        {
                            uri: style.previewImageUrl,
                            type: "image/png",
                        },
                    ],
                    category: "image",
                },
            };

            // 2) Upload metadata JSON to Irys
            setStatus("📡 Uploading style metadata to Irys…");
            const mdUri = await umi.uploader.uploadJson(metadata);
            if (!mdUri) throw new Error("Style metadata upload failed.");

            // 3) Build transaction: transfer SOL (price) -> create Core NFT
            setStatus("🧾 Building purchase transaction…");
            const asset = generateSigner(umi);

            // Convert stored lamports → SOL
            const basePriceSol =
                typeof style.priceLamports === "number"
                    ? style.priceLamports / 1_000_000_000
                    : Number(style.priceSol || 0);

            // Final cost the user must pay
            const finalPriceSol = basePriceSol + FIXED_FEE_SOL;

            setStatus(
                `🧾 Purchase cost: ${finalPriceSol.toFixed(
                    5
                )} SOL (includes ~0.005 fee)`
            );

            // Build transaction
            let builder = transactionBuilder();

            if (finalPriceSol > 0) {
                builder = builder.add(
                    transferSol(umi, {
                        destination: umiPublicKey(STYLE_SERVICE_WALLET),
                        amount: sol(finalPriceSol), // pay full price with fee
                    })
                );
            }

            builder = builder.add(
                createCoreNft(umi, {
                    asset,
                    name: metadata.name,
                    uri: mdUri,
                })
            );

            setStatus("🚀 Sending transaction…");
            const receipt = await builder.sendAndConfirm(umi);

            // 4) Extract tx signature (base58)
            let sigStr = "";
            if (typeof receipt === "string") {
                sigStr = receipt;
            } else if (typeof receipt?.signature === "string") {
                sigStr = receipt.signature;
            } else if (receipt?.signature) {
                sigStr = base58.deserialize(receipt.signature)[0];
            }

            const buyerPk = publicKey.toBase58();

            // 5) Record purchase in Firestore
            await addDoc(collection(db, "style_purchases"), {
                txSignature: sigStr,
                buyer: buyerPk,
                styleId: style.id,
                basePriceLamports: style.priceLamports,
                finalPriceLamports: Math.floor(
                    finalPriceSol * 1_000_000_000
                ),
                status: "success",
                metadataUri: mdUri,
                imageUri: style.previewImageUrl,
                assetPubkey: asset.publicKey.toString(),
                createdAt: serverTimestamp(),
            });

            // 6) Unlock style for this user
            const userDocRef = doc(db, "user_styles", buyerPk);
            await setDoc(
                userDocRef,
                {
                    styles: arrayUnion(style.id),
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );

            setStatus(
                `✅ Purchased ${metadata.name}. The style should now be unlocked in your style switcher.`
            );
        } catch (err) {
            console.error("Error purchasing style:", err);
            setStatus(`❌ Purchase failed: ${err.message || String(err)}`);

            if (publicKey) {
                await addDoc(collection(db, "style_purchases"), {
                    buyer: publicKey.toBase58(),
                    styleId: style.id,
                    status: "failed",
                    error: String(err.message || err),
                    createdAt: serverTimestamp(),
                });
            }
        } finally {
            setIsPurchasing(false);
        }
    };

    return (
        <div className="wallet-explorer-root theme-og-retro">
            <h1>Style Shop</h1>
            <h3>
                Purchase a style to unlock new looks for The Crumb NFT Wallet Explorer.
                Receive an NFT as the proof-of-purchase receipt.
                The wallet used to purchase the style, not the NFT, unlocks the style.
                The NFT is just the record..... for now 🔧.
            </h3>

            {/* Status section */}
            {status && (
                <div
                    style={{
                        padding: "10px",
                        background: "#222",
                        color: "#fff",
                        borderRadius: "6px",
                        marginBottom: "15px",
                        border: "1px solid #555",
                    }}
                >
                    {status}
                </div>
            )}

            {!connected && <p>Connect your wallet to purchase styles.</p>}

            {styles.map((style) => (
                <div key={style.id} className="transaction-item">
                    <strong>{style.name || style.id}</strong>
                    <p>{style.description}</p>

                    {style.previewImageUrl && (
                        <img
                            src={style.previewImageUrl}
                            alt={style.name || style.id}
                            style={{ maxWidth: "220px", borderRadius: 8 }}
                        />
                    )}

                    {/* ⭐ Screenshot thumbnails */}
                    {Array.isArray(style.screenshotUrls) &&
                    style.screenshotUrls.length > 0 && (
                        <div className="style-screenshot-strip">
                            {style.screenshotUrls.map((url, idx) => (
                                <img
                                    key={idx}
                                    src={url}
                                    alt={`${style.name || style.id} screenshot ${
                                        idx + 1
                                    }`}
                                    className="style-screenshot-thumb"
                                />
                            ))}
                        </div>
                    )}

                    <p>
                        Price:{" "}
                        {(
                            (style.priceLamports || 0) / 1_000_000_000 +
                            FIXED_FEE_SOL
                        ).toFixed(4)}{" "}
                        SOL
                        <br />
                        <small>(includes ~0.005 SOL fee)</small>
                    </p>

                    <button
                        onClick={() => handlePurchase(style)}
                        disabled={isPurchasing || !connected}
                    >
                        {isPurchasing ? "Processing…" : "Purchase Style"}
                    </button>
                </div>
            ))}
        </div>
    );
}
